import {
  ApplicantEmploymentHistoryV2Dto,
  ApplicantMinorV2Dto,
  ApplicantPetV2Dto,
  ApplicantRentalHistoryV2Dto,
  ApplicantV2Dto,
  ApplicantVehicleV2Dto,
  ApplicationAcceptedTsCsAuditV2Dto,
  ApplicationTsCsDto,
  ApplicationInviteeDetailV2Dto,
  ApplicationQuestionResponseV2Dto,
  ApplicationV2Dto,
  ReadDocumentDto,
  ApplicationSummaryDto,
  UpdateApplicantStatusResponseDto,
} from '@inosio/payments-sdk';
import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../../app/store';

/**
 * This slice is intended to be used solely for the applicants flow.
 * There should be no PM UI functionality coming through this at all.
 */

//list everything that we need to track at the applicant level
type applicantsState = {
  Applicant: ApplicantV2Dto;
  isFetchingApplicant: boolean;
  isFetchingApplicantSuccess: boolean;
  isFetchingApplicantFailure: boolean;
  isFetchingApplicantError: ErrorType;
  isCreatingAnApplicant: boolean;
  isCreateApplicantSuccess: boolean;
  isCreateApplicantFailed: boolean;
  createApplicantError: ErrorType;
  InvitedApplicant: ApplicantV2Dto;
  isInvitingApplicant: boolean;
  isInvitingApplicantSuccess: boolean;
  isInvitingApplicantFailed: boolean;
  inviteApplicantError: ErrorType;
  ApplicationInviteeDetailV2Dto: ApplicationInviteeDetailV2Dto;
  isFetchingApplicationInviteeInfo: boolean;
  isFetchingApplicationInviteeInfoSuccess: boolean;
  isFetchingApplicationInviteeInfoFailed: boolean;
  isFetchingApplicationInviteeInfoError: ErrorType;
  isRemovingApplicant: boolean;
  isRemovingApplicantSuccess: boolean;
  isRemovingApplicantFailed: boolean;
  isRemovingApplicantError: ErrorType;
  isUpdatingApplicant: boolean;
  isUpdatingApplicantSuccess: boolean;
  isUpdatingApplicantFailed: boolean;
  updatingApplicantError: ErrorType;
  Application: ApplicationV2Dto; //* the live application that Apollo is working on right now.
  isCreatingAnApplication: boolean;
  isCreateApplicationSuccess: boolean;
  isCreateApplicationFailed: boolean;
  createApplicationError: ErrorType;
  //update Application
  isUpdatingApplication: boolean;
  isUpdatingApplicationSuccess: boolean;
  isUpdatingApplicationFailed: boolean;
  updateApplicationError: ErrorType;
  isFetchingApplication: boolean;
  isFetchingApplicationSuccess: boolean;
  isFetchingApplicationFailure: boolean;
  isFetchingApplicationError: ErrorType;
  CustomQuestionAnswers: ApplicationQuestionResponseV2Dto[];
  isFetchingCustomQuestionAnswers: boolean;
  isFetchingCustomQuestionAnswersSuccess: boolean;
  isFetchingCustomQuestionAnswersFailure: boolean;
  isFetchingCustomQuestionAnswersError: ErrorType;
  isUpsertingCustomQuestionAnswers: boolean;
  isUpsertingCustomQuestionAnswersSuccess: boolean;
  isUpsertingCustomQuestionAnswersFailed: boolean;
  isUpsertingCustomQuestionAnswersError: ErrorType;
  fetchDocumentResponse: ReadDocumentDto[];
  isFetchingDocuments: boolean;
  isFetchingDocumentsSuccess: boolean;
  isFetchingDocumentsFailed: boolean;
  fetchDocumentError: ErrorType;
  EmploymentIncomeRecords: ApplicantEmploymentHistoryV2Dto[];
  isFetchingEmploymentIncomeRecords: boolean;
  isFetchingEmploymentIncomeRecordsSuccess: boolean;
  isFetchingEmploymentIncomeRecordsFailed: boolean;
  fetchEmploymentIncomeRecordsError: ErrorType;
  isDeletingEmploymentIncomeRecord: boolean;
  isDeletingEmploymentIncomeRecordSuccess: boolean;
  isDeletingEmploymentIncomeRecordFailed: boolean;
  isDeletingEmploymentIncomeRecordError: ErrorType;
  createdEmploymentIncomeRecord: ApplicantEmploymentHistoryV2Dto | null; //also use for updated
  isCreatingEmploymentIncomeRecord: boolean;
  isCreatingEmploymentIncomeRecordSuccess: boolean;
  isCreatingEmploymentIncomeRecordFailed: boolean;
  isCreatingEmploymentIncomeRecordError: ErrorType;
  isUpdatingEmploymentIncomeRecord: boolean;
  isUpdatingEmploymentIncomeRecordSuccess: boolean;
  isUpdatingEmploymentIncomeRecordFailed: boolean;
  isUpdatingEmploymentIncomeRecordError: ErrorType;
  MinorRecords: ApplicantMinorV2Dto[];
  isFetchingMinorRecords: boolean;
  isFetchingMinorRecordsSuccess: boolean;
  isFetchingMinorRecordsFailed: boolean;
  fetchMinorRecordsError: ErrorType;
  isDeletingMinorRecord: boolean;
  isDeletingMinorRecordSuccess: boolean;
  isDeletingMinorRecordFailed: boolean;
  isDeletingMinorRecordError: ErrorType;
  createdMinorRecord: ApplicantMinorV2Dto | null; //also use for updated
  isCreatingMinorRecord: boolean;
  isCreatingMinorRecordSuccess: boolean;
  isCreatingMinorRecordFailed: boolean;
  isCreatingMinorRecordError: ErrorType;
  isUpdatingMinorRecord: boolean;
  isUpdatingMinorRecordSuccess: boolean;
  isUpdatingMinorRecordFailed: boolean;
  isUpdatingMinorRecordError: ErrorType;
  //personal info
  //employments

  //#region rental history

  isCreatingRentalHistoryRecord: boolean;
  isCreatingRentalHistoryRecordFailed: boolean;
  isCreatingRentalHistoryRecordSuccess: boolean;
  createRentalHistoryRecordError: ErrorType;
  createdRentalHistoryRecord: ApplicantRentalHistoryV2Dto | null;
  //fetch all rental history records
  isFetchingAllRentalHistoryRecords: boolean;
  isFetchingAllRentalHistoryRecordsFailed: boolean;
  isFetchingAllRentalHistoryRecordsSuccess: boolean;
  fetchAllRentalHistoryRecordsError: ErrorType;
  allRentalHistoryRecords: ApplicantRentalHistoryV2Dto[];
  //fetch a single rental history record given its uuid
  isFetchingRentalHistoryRecord: boolean;
  isFetchingRentalHistoryRecordFailed: boolean;
  isFetchingRentalHistoryRecordSuccess: boolean;
  fetchedRentalHistoryRecord: ApplicantRentalHistoryV2Dto;
  fetchRentalHistoryRecordError: ErrorType;
  //delete a single rental history record
  isDeletingRentalHistoryRecord: boolean;
  isDeletingRentalHistoryRecordFailed: boolean;
  isDeletingRentalHistoryRecordSuccess: boolean;
  deleteRentalHistoryRecordError: ErrorType;
  //update a single rental history record
  isUpdatingRentalHistoryRecord: boolean;
  isUpdatingRentalHistoryRecordSuccess: boolean;
  isUpdatingRentalHistoryRecordFailed: boolean;
  updateRentalHistoryRecordError: ErrorType;

  //#end region rental history

  //#region pets

  //create pet record
  isCreatingApplicantPetRecord: boolean;
  isCreatingApplicantPetRecordFailed: boolean;
  isCreatingApplicantPetRecordSuccess: boolean;
  createApplicantPetRecordError: ErrorType;
  createdApplicantPetRecord: ApplicantPetV2Dto | null;

  //get all pet records for this applicant
  isFetchingApplicantAllPetRecords: boolean;
  isFetchingApplicantAllPetRecordsFailed: boolean;
  isFetchingApplicantAllPetRecordsSuccess: boolean;
  fetchApplicantAllPetRecordsError: ErrorType;
  applicantAllPetRecords: ApplicantPetV2Dto[];
  // get a single pet record for this applicant
  isFetchingApplicantPetRecord: boolean;
  isFetchingApplicantPetRecordFailed: boolean;
  isFetchingApplicantPetRecordSuccess: boolean;
  fetchApplicantPetRecordError: ErrorType;
  applicantPetRecord: ApplicantPetV2Dto;
  // delete a single pet record for this applicant
  isDeletingApplicantPetRecord: boolean;
  isDeletingApplicantPetRecordFailed: boolean;
  isDeletingApplicantPetRecordSuccess: boolean;
  deleteApplicantPetRecordError: ErrorType;
  // update a single pet record for this applicant
  isUpdatingApplicantPetRecord: boolean;
  isUpdatingApplicantPetRecordFailed: boolean;
  isUpdatingApplicantPetRecordSuccess: boolean;
  updateApplicantPetRecordError: ErrorType;

  //#end region pets

  //# region vehicles

  //create Vehicle record
  isCreatingApplicantVehicleRecord: boolean;
  isCreatingApplicantVehicleRecordFailed: boolean;
  isCreatingApplicantVehicleRecordSuccess: boolean;
  createApplicantVehicleRecordError: ErrorType;
  createdApplicantVehicleRecord: ApplicantVehicleV2Dto | null;

  //get all Vehicle records for this applicant
  isFetchingApplicantAllVehicleRecords: boolean;
  isFetchingApplicantAllVehicleRecordsFailed: boolean;
  isFetchingApplicantAllVehicleRecordsSuccess: boolean;
  fetchApplicantAllVehicleRecordsError: ErrorType;
  applicantAllVehicleRecords: ApplicantVehicleV2Dto[];
  // get a single Vehicle record for this applicant
  isFetchingApplicantVehicleRecord: boolean;
  isFetchingApplicantVehicleRecordFailed: boolean;
  isFetchingApplicantVehicleRecordSuccess: boolean;
  fetchApplicantVehicleRecordError: ErrorType;
  applicantVehicleRecord: ApplicantVehicleV2Dto;
  // delete a single Vehicle record for this applicant
  isDeletingApplicantVehicleRecord: boolean;
  isDeletingApplicantVehicleRecordFailed: boolean;
  isDeletingApplicantVehicleRecordSuccess: boolean;
  deleteApplicantVehicleRecordError: ErrorType;
  // update a single Vehicle record for this applicant
  isUpdatingApplicantVehicleRecord: boolean;
  isUpdatingApplicantVehicleRecordFailed: boolean;
  isUpdatingApplicantVehicleRecordSuccess: boolean;
  updateApplicantVehicleRecordError: ErrorType;

  //# end region vehicles
  isAddingAcceptedTCsToJson: boolean;
  isAddingAcceptedTCsToJsonFailed: boolean;
  isAddingAcceptedTCsToJsonSuccess: boolean;
  addingAcceptedTCsToJsonError: ErrorType;
  isGettingApplicationAcceptedTCs: boolean;
  isGettingApplicationAcceptedTCsSuccess: boolean;
  isGettingApplicationAcceptedTCsFailed: boolean;
  gettingApplicationAcceptedTCsError: ErrorType;
  applicationAcceptedTCs: ApplicationAcceptedTsCsAuditV2Dto[];

  //These are company terms (rental, employment, background check) that are to be accepted by each applicant
  isFetchingOrganizationApplicationTCs: boolean;
  isFetchingOrganizationApplicationTCsSuccess: boolean;
  isFetchingOrganizationApplicationTCsFailed: boolean;
  fetchOrganizationApplicationTCsError: ErrorType;
  organizationApplicationTCs: ApplicationTsCsDto;

  //applicant status change
  isUpdatingApplicantStatus: boolean;
  isUpdatingApplicantStatusSuccess: boolean;
  isUpdatingApplicantStatusFailed: boolean;
  isUpdatingApplicantStatusError: ErrorType;
  updateApplicantStatusResponse: UpdateApplicantStatusResponseDto | null;

  applicationsFetchResponse: ApplicationSummaryDto[];
  isFetchingApplications: boolean;
  isFetchingApplicationsSuccess: boolean;
  isFetchingApplicationsFailed: boolean;
  isFetchingApplicationsError: ErrorType;
};

const getInitialState = (): applicantsState => ({
  Applicant: {} as ApplicantV2Dto,
  isFetchingApplicant: false,
  isFetchingApplicantSuccess: false,
  isFetchingApplicantFailure: false,
  isFetchingApplicantError: {} as ErrorType,
  isCreatingAnApplicant: false,
  isCreateApplicantSuccess: false,
  isCreateApplicantFailed: false,
  createApplicantError: {} as ErrorType,
  InvitedApplicant: {} as ApplicantV2Dto,
  isInvitingApplicant: false,
  isInvitingApplicantSuccess: false,
  isInvitingApplicantFailed: false,
  inviteApplicantError: {} as ErrorType,
  ApplicationInviteeDetailV2Dto: {} as ApplicationInviteeDetailV2Dto,
  isFetchingApplicationInviteeInfo: false,
  isFetchingApplicationInviteeInfoSuccess: false,
  isFetchingApplicationInviteeInfoFailed: false,
  isFetchingApplicationInviteeInfoError: {} as ErrorType,
  isRemovingApplicant: false,
  isRemovingApplicantSuccess: false,
  isRemovingApplicantFailed: false,
  isRemovingApplicantError: {} as ErrorType,
  Application: {} as ApplicationV2Dto,
  createApplicationError: {} as ErrorType,
  isCreateApplicationFailed: false,
  isCreateApplicationSuccess: false,
  isCreatingAnApplication: false,
  isUpdatingApplicant: false,
  isUpdatingApplicantSuccess: false,
  isUpdatingApplicantFailed: false,
  updatingApplicantError: {} as ErrorType,
  isUpdatingApplication: false,
  isUpdatingApplicationSuccess: false,
  isUpdatingApplicationFailed: false,
  updateApplicationError: {} as ErrorType,
  /// acknowledgements
  isAddingAcceptedTCsToJson: false,
  isAddingAcceptedTCsToJsonFailed: false,
  isAddingAcceptedTCsToJsonSuccess: false,
  addingAcceptedTCsToJsonError: {} as ErrorType,
  isGettingApplicationAcceptedTCs: false,
  isGettingApplicationAcceptedTCsSuccess: false,
  isGettingApplicationAcceptedTCsFailed: false,
  gettingApplicationAcceptedTCsError: {} as ErrorType,
  applicationAcceptedTCs: [] as ApplicationAcceptedTsCsAuditV2Dto[],
  isFetchingApplication: false,
  isFetchingApplicationSuccess: false,
  isFetchingApplicationFailure: false,
  isFetchingApplicationError: {} as ErrorType,
  CustomQuestionAnswers: [],
  isFetchingCustomQuestionAnswers: false,
  isFetchingCustomQuestionAnswersSuccess: false,
  isFetchingCustomQuestionAnswersFailure: false,
  isFetchingCustomQuestionAnswersError: {} as ErrorType,
  isUpsertingCustomQuestionAnswers: false,
  isUpsertingCustomQuestionAnswersSuccess: false,
  isUpsertingCustomQuestionAnswersFailed: false,
  isUpsertingCustomQuestionAnswersError: {} as ErrorType,
  isFetchingOrganizationApplicationTCs: false,
  isFetchingOrganizationApplicationTCsSuccess: false,
  isFetchingOrganizationApplicationTCsFailed: false,
  fetchOrganizationApplicationTCsError: {} as ErrorType,
  organizationApplicationTCs: {} as ApplicationTsCsDto,
  fetchDocumentResponse: [],
  isFetchingDocuments: false,
  isFetchingDocumentsSuccess: false,
  isFetchingDocumentsFailed: false,
  fetchDocumentError: {} as ErrorType,
  EmploymentIncomeRecords: [],
  isFetchingEmploymentIncomeRecords: false,
  isFetchingEmploymentIncomeRecordsSuccess: false,
  isFetchingEmploymentIncomeRecordsFailed: false,
  fetchEmploymentIncomeRecordsError: {} as ErrorType,
  isDeletingEmploymentIncomeRecord: false,
  isDeletingEmploymentIncomeRecordSuccess: false,
  isDeletingEmploymentIncomeRecordFailed: false,
  isDeletingEmploymentIncomeRecordError: {} as ErrorType,
  createdEmploymentIncomeRecord: null,
  isCreatingEmploymentIncomeRecord: false,
  isCreatingEmploymentIncomeRecordSuccess: false,
  isCreatingEmploymentIncomeRecordFailed: false,
  isCreatingEmploymentIncomeRecordError: {} as ErrorType,
  isUpdatingEmploymentIncomeRecord: false,
  isUpdatingEmploymentIncomeRecordSuccess: false,
  isUpdatingEmploymentIncomeRecordFailed: false,
  isUpdatingEmploymentIncomeRecordError: {} as ErrorType,
  isCreatingRentalHistoryRecord: false,
  isCreatingRentalHistoryRecordFailed: false,
  isCreatingRentalHistoryRecordSuccess: false,
  createRentalHistoryRecordError: {} as ErrorType,
  createdRentalHistoryRecord: null,
  isFetchingAllRentalHistoryRecords: false,
  isFetchingAllRentalHistoryRecordsFailed: false,
  isFetchingAllRentalHistoryRecordsSuccess: false,
  fetchAllRentalHistoryRecordsError: {} as ErrorType,
  allRentalHistoryRecords: [] as ApplicantRentalHistoryV2Dto[],
  isFetchingRentalHistoryRecord: false,
  isFetchingRentalHistoryRecordFailed: false,
  isFetchingRentalHistoryRecordSuccess: false,
  fetchedRentalHistoryRecord: {} as ApplicantRentalHistoryV2Dto,
  fetchRentalHistoryRecordError: {} as ErrorType,
  isDeletingRentalHistoryRecord: false,
  isDeletingRentalHistoryRecordFailed: false,
  isDeletingRentalHistoryRecordSuccess: false,
  deleteRentalHistoryRecordError: {} as ErrorType,
  isUpdatingRentalHistoryRecord: false,
  isUpdatingRentalHistoryRecordSuccess: false,
  isUpdatingRentalHistoryRecordFailed: false,
  updateRentalHistoryRecordError: {} as ErrorType,
  MinorRecords: [],
  isFetchingMinorRecords: false,
  isFetchingMinorRecordsSuccess: false,
  isFetchingMinorRecordsFailed: false,
  fetchMinorRecordsError: {} as ErrorType,
  isDeletingMinorRecord: false,
  isDeletingMinorRecordSuccess: false,
  isDeletingMinorRecordFailed: false,
  isDeletingMinorRecordError: {} as ErrorType,
  createdMinorRecord: null,
  isCreatingMinorRecord: false,
  isCreatingMinorRecordSuccess: false,
  isCreatingMinorRecordFailed: false,
  isCreatingMinorRecordError: {} as ErrorType,
  isUpdatingMinorRecord: false,
  isUpdatingMinorRecordSuccess: false,
  isUpdatingMinorRecordFailed: false,
  isUpdatingMinorRecordError: {} as ErrorType,
  isCreatingApplicantPetRecord: false,
  isCreatingApplicantPetRecordFailed: false,
  isCreatingApplicantPetRecordSuccess: false,
  createApplicantPetRecordError: {} as ErrorType,
  createdApplicantPetRecord: null,
  isFetchingApplicantAllPetRecords: false,
  isFetchingApplicantAllPetRecordsFailed: false,
  isFetchingApplicantAllPetRecordsSuccess: false,
  applicantAllPetRecords: [] as ApplicantPetV2Dto[],
  fetchApplicantAllPetRecordsError: {} as ErrorType,
  isFetchingApplicantPetRecord: false,
  isFetchingApplicantPetRecordFailed: false,
  isFetchingApplicantPetRecordSuccess: false,
  fetchApplicantPetRecordError: {} as ErrorType,
  applicantPetRecord: {} as ApplicantPetV2Dto,
  isDeletingApplicantPetRecord: false,
  isDeletingApplicantPetRecordFailed: false,
  isDeletingApplicantPetRecordSuccess: false,
  deleteApplicantPetRecordError: {} as ErrorType,
  isUpdatingApplicantPetRecord: false,
  isUpdatingApplicantPetRecordFailed: false,
  isUpdatingApplicantPetRecordSuccess: false,
  updateApplicantPetRecordError: {} as ErrorType,
  //Vehicles
  isCreatingApplicantVehicleRecord: false,
  isCreatingApplicantVehicleRecordFailed: false,
  isCreatingApplicantVehicleRecordSuccess: false,
  createApplicantVehicleRecordError: {} as ErrorType,
  createdApplicantVehicleRecord: null,
  isFetchingApplicantAllVehicleRecords: false,
  isFetchingApplicantAllVehicleRecordsFailed: false,
  isFetchingApplicantAllVehicleRecordsSuccess: false,
  applicantAllVehicleRecords: [] as ApplicantVehicleV2Dto[],
  fetchApplicantAllVehicleRecordsError: {} as ErrorType,
  isFetchingApplicantVehicleRecord: false,
  isFetchingApplicantVehicleRecordFailed: false,
  isFetchingApplicantVehicleRecordSuccess: false,
  fetchApplicantVehicleRecordError: {} as ErrorType,
  applicantVehicleRecord: {} as ApplicantVehicleV2Dto,
  isDeletingApplicantVehicleRecord: false,
  isDeletingApplicantVehicleRecordFailed: false,
  isDeletingApplicantVehicleRecordSuccess: false,
  deleteApplicantVehicleRecordError: {} as ErrorType,
  isUpdatingApplicantVehicleRecord: false,
  isUpdatingApplicantVehicleRecordFailed: false,
  isUpdatingApplicantVehicleRecordSuccess: false,
  updateApplicantVehicleRecordError: {} as ErrorType,
  isUpdatingApplicantStatus: false,
  isUpdatingApplicantStatusSuccess: false,
  isUpdatingApplicantStatusFailed: false,
  isUpdatingApplicantStatusError: {} as ErrorType,
  updateApplicantStatusResponse: null,
  applicationsFetchResponse: [] as ApplicationSummaryDto[],
  isFetchingApplications: false,
  isFetchingApplicationsSuccess: false,
  isFetchingApplicationsFailed: false,
  isFetchingApplicationsError: {} as ErrorType,
});

export const applicantsSlice = createSlice({
  name: 'applicantsSlice',
  initialState: getInitialState(),
  reducers: {
    resetEntireApplicantState(state) {
      return { ...getInitialState() };
    },
    getApplicantV2(state) {
      state.isFetchingApplicant = true;
      state.isFetchingApplicantSuccess = false;
      state.isFetchingApplicantFailure = false;
      state.isFetchingApplicantError = {} as ErrorType;
    },
    setApplicantV2(state, action) {
      state.Applicant = action.payload;
      state.isFetchingApplicant = false;
      state.isFetchingApplicantSuccess = true;
      state.isFetchingApplicantFailure = false;
      state.isFetchingApplicantError = {} as ErrorType;
    },
    setApplicantV2Error(state, action) {
      state.isFetchingApplicant = false;
      state.isFetchingApplicantSuccess = false;
      state.isFetchingApplicantFailure = true;
      state.isFetchingApplicantError = action.payload as ErrorType;
    },
    createApplicantV2(state, action) {
      state.isCreatingAnApplicant = true;
      state.isCreateApplicantSuccess = false;
      state.isCreateApplicantFailed = false;
      state.createApplicantError = {} as ErrorType;
    },
    setCreateApplicantV2(state, action) {
      state.Applicant = action.payload;
      state.isCreatingAnApplicant = false;
      state.isCreateApplicantSuccess = true;
      state.isCreateApplicantFailed = false;
      state.createApplicantError = {} as ErrorType;
    },
    setCreateApplicantV2Error(state, action) {
      state.isCreateApplicantSuccess = false;
      state.isCreateApplicantFailed = true;
      state.isCreatingAnApplicant = false;
      state.createApplicantError = action.payload as ErrorType;
    },
    inviteApplicantV2(state, action) {
      state.isInvitingApplicant = true;
      state.isInvitingApplicantSuccess = false;
      state.isInvitingApplicantFailed = false;
      state.inviteApplicantError = {} as ErrorType;
    },
    setInviteApplicantV2(state, action) {
      state.InvitedApplicant = action.payload;
      state.isInvitingApplicant = false;
      state.isInvitingApplicantSuccess = true;
      state.isInvitingApplicantFailed = false;
      state.inviteApplicantError = {} as ErrorType;
    },
    setInviteApplicantV2Error(state, action) {
      state.isInvitingApplicantSuccess = false;
      state.isInvitingApplicantFailed = true;
      state.isInvitingApplicant = false;
      state.inviteApplicantError = action.payload as ErrorType;
    },
    resetInviteApplicantState(state) {
      state.isInvitingApplicantSuccess = false;
      state.isInvitingApplicantFailed = false;
      state.inviteApplicantError = {} as ErrorType;
    },
    removeApplicantV2(state) {
      state.isRemovingApplicant = true;
      state.isRemovingApplicantSuccess = false;
      state.isRemovingApplicantFailed = false;
      state.isRemovingApplicantError = {} as ErrorType;
    },
    setRemoveApplicantV2(state, action) {
      state.isRemovingApplicant = false;
      state.isRemovingApplicantSuccess = true;
      state.isRemovingApplicantFailed = false;
      state.isRemovingApplicantError = {} as ErrorType;
    },
    setRemoveApplicantV2Error(state, action) {
      state.isRemovingApplicant = false;
      state.isRemovingApplicantSuccess = false;
      state.isRemovingApplicantFailed = true;
      state.isRemovingApplicantError = action.payload as ErrorType;
    },
    getApplicationIntiteeInfoV2(state, action) {
      state.isFetchingApplicationInviteeInfo = true;
      state.isFetchingApplicationInviteeInfoSuccess = false;
      state.isFetchingApplicationInviteeInfoFailed = false;
      state.isFetchingApplicationInviteeInfoError = {} as ErrorType;
    },
    setApplicationIntiteeInfoV2(state, action) {
      state.ApplicationInviteeDetailV2Dto = action.payload;
      state.isFetchingApplicationInviteeInfo = false;
      state.isFetchingApplicationInviteeInfoSuccess = true;
      state.isFetchingApplicationInviteeInfoFailed = false;
      state.isFetchingApplicationInviteeInfoError = {} as ErrorType;
    },
    setApplicationIntiteeInfoV2Error(state, action) {
      state.isFetchingApplicationInviteeInfoSuccess = false;
      state.isFetchingApplicationInviteeInfoFailed = true;
      state.isFetchingApplicationInviteeInfo = false;
      state.isFetchingApplicationInviteeInfoError = action.payload as ErrorType;
    },
    updateApplicantV2(state, action) {
      state.isUpdatingApplicant = true;
      state.isUpdatingApplicantSuccess = false;
      state.isUpdatingApplicantFailed = false;
      state.updatingApplicantError = {} as ErrorType;
    },
    setUpdateApplicantV2(state, action) {
      state.isUpdatingApplicant = false;
      state.isUpdatingApplicantSuccess = true;
      state.isUpdatingApplicantFailed = false;
      state.updatingApplicantError = {} as ErrorType;
    },
    setUpdateApplicantV2Error(state, action) {
      state.isUpdatingApplicant = false;
      state.isUpdatingApplicantSuccess = false;
      state.isUpdatingApplicantFailed = true;
      state.updatingApplicantError = action.payload as ErrorType;
    },
    clearUpdateApplicantV2(state) {
      state.isUpdatingApplicant = false;
      state.isUpdatingApplicantSuccess = false;
      state.isUpdatingApplicantFailed = false;
      state.updatingApplicantError = {} as ErrorType;
    },
    createApplicationV2(state, action) {
      state.isCreatingAnApplication = true;
      state.isCreateApplicationSuccess = false;
      state.isCreateApplicationFailed = false;
      state.createApplicationError = {} as ErrorType;
    },
    setCreateApplicationV2(state, action) {
      state.Application = action.payload;
      state.isCreatingAnApplication = false;
      state.isCreateApplicationSuccess = true;
      state.isCreateApplicationFailed = false;
      state.createApplicationError = {} as ErrorType;
    },
    setCreateApplicationV2Error(state, action) {
      state.isCreateApplicationSuccess = false;
      state.isCreateApplicationFailed = true;
      state.isCreatingAnApplication = false;
      state.createApplicationError = action.payload as ErrorType;
    },
    updateApplicationV2(state) {
      state.isUpdatingApplication = true;
      state.isUpdatingApplicationFailed = false;
      state.isUpdatingApplicationSuccess = false;
      state.updateApplicationError = {} as ErrorType;
    },
    setUpdateApplicationV2(state) {
      state.isUpdatingApplication = false;
      state.isUpdatingApplicationFailed = false;
      state.isUpdatingApplicationSuccess = true;
    },
    setUpdateApplicationErrorV2(state, action) {
      state.isUpdatingApplication = false;
      state.isUpdatingApplicationFailed = false;
      state.isUpdatingApplicationSuccess = false;
      state.updateApplicationError = action.payload;
    },
    addAcceptedTCsToJsonV2(state) {
      state.isAddingAcceptedTCsToJson = true;
      state.isAddingAcceptedTCsToJsonFailed = false;
      state.isAddingAcceptedTCsToJsonSuccess = false;
      state.addingAcceptedTCsToJsonError = {} as ErrorType;
    },
    setAddAcceptedTCsToJsonV2(state) {
      state.isAddingAcceptedTCsToJson = false;
      state.isAddingAcceptedTCsToJsonFailed = false;
      state.isAddingAcceptedTCsToJsonSuccess = true;
      state.addingAcceptedTCsToJsonError = {} as ErrorType;
    },
    setAddAcceptedTCsToJsonErrorV2(state, action) {
      state.isAddingAcceptedTCsToJson = false;
      state.isAddingAcceptedTCsToJsonFailed = true;
      state.isAddingAcceptedTCsToJsonSuccess = false;
      state.addingAcceptedTCsToJsonError = action.payload as ErrorType;
    },
    getApplicationAcceptedTCsV2(state) {
      state.isGettingApplicationAcceptedTCs = true;
      state.isGettingApplicationAcceptedTCsSuccess = false;
      state.isGettingApplicationAcceptedTCsFailed = false;
      state.applicationAcceptedTCs = [] as ApplicationAcceptedTsCsAuditV2Dto[];
      state.gettingApplicationAcceptedTCsError = {} as ErrorType;
    },
    setApplicationAcceptedTCsV2(state, action) {
      state.isGettingApplicationAcceptedTCs = false;
      state.isGettingApplicationAcceptedTCsSuccess = true;
      state.isGettingApplicationAcceptedTCsFailed = false;
      state.gettingApplicationAcceptedTCsError = {} as ErrorType;
      state.applicationAcceptedTCs = action.payload;
    },
    setApplicationAcceptedTCsErrorV2(state, action) {
      state.isGettingApplicationAcceptedTCs = false;
      state.isGettingApplicationAcceptedTCsSuccess = false;
      state.isGettingApplicationAcceptedTCsFailed = true;
      state.gettingApplicationAcceptedTCsError = action.payload;
    },
    getApplicationV2(state, action) {
      state.isFetchingApplication = true;
      state.isFetchingApplicationSuccess = false;
      state.isFetchingApplicationFailure = false;
      state.isFetchingApplicationError = {} as ErrorType;
    },
    setApplicationV2(state, action) {
      state.Application = action.payload;
      state.isFetchingApplication = false;
      state.isFetchingApplicationSuccess = true;
      state.isFetchingApplicationFailure = false;
      state.isFetchingApplicationError = {} as ErrorType;
    },
    setApplicationV2Error(state, action) {
      state.isFetchingApplication = false;
      state.isFetchingApplicationSuccess = false;
      state.isFetchingApplicationFailure = true;
      state.isFetchingApplicationError = action.payload as ErrorType;
    },
    getCustomQuestionsAnswersV2(state, action) {
      state.isFetchingCustomQuestionAnswers = true;
      state.isFetchingCustomQuestionAnswersSuccess = false;
      state.isFetchingCustomQuestionAnswersFailure = false;
      state.isFetchingCustomQuestionAnswersError = {} as ErrorType;
    },
    setCustomQuestionsAnswersV2(state, action) {
      state.CustomQuestionAnswers = action.payload;
      state.isFetchingCustomQuestionAnswers = false;
      state.isFetchingCustomQuestionAnswersSuccess = true;
      state.isFetchingCustomQuestionAnswersFailure = false;
      state.isFetchingCustomQuestionAnswersError = {} as ErrorType;
    },
    setCustomQuestionsAnswersV2Error(state, action) {
      state.isFetchingCustomQuestionAnswers = false;
      state.isFetchingCustomQuestionAnswersSuccess = false;
      state.isFetchingCustomQuestionAnswersFailure = true;
      state.isFetchingCustomQuestionAnswersError = action.payload as ErrorType;
    },
    updateCustomQuestionsAnswersV2(state, action) {
      state.isUpsertingCustomQuestionAnswers = true;
      state.isUpsertingCustomQuestionAnswersSuccess = false;
      state.isUpsertingCustomQuestionAnswersFailed = false;
      state.isUpsertingCustomQuestionAnswersError = {} as ErrorType;
    },
    setUpdateCustomQuestionsAnswersV2(state, action) {
      state.isUpsertingCustomQuestionAnswers = false;
      state.isUpsertingCustomQuestionAnswersSuccess = true;
      state.isUpsertingCustomQuestionAnswersFailed = false;
      state.isUpsertingCustomQuestionAnswersError = {} as ErrorType;
    },
    setUpdateCustomQuestionsAnswersV2Error(state, action) {
      state.isUpsertingCustomQuestionAnswers = false;
      state.isUpsertingCustomQuestionAnswersSuccess = false;
      state.isUpsertingCustomQuestionAnswersFailed = true;
      state.isUpsertingCustomQuestionAnswersError = action.payload as ErrorType;
    },
    getOrganizationApplicationTCs(state) {
      state.isFetchingOrganizationApplicationTCs = true;
      state.isFetchingOrganizationApplicationTCsSuccess = false;
      state.isFetchingOrganizationApplicationTCsFailed = false;
      state.fetchOrganizationApplicationTCsError = {} as ErrorType;
    },
    setOrganizationApplicationTCs(state, action) {
      state.isFetchingOrganizationApplicationTCs = false;
      state.isFetchingOrganizationApplicationTCsSuccess = true;
      state.isFetchingOrganizationApplicationTCsFailed = false;
      state.organizationApplicationTCs = action.payload;
    },
    setOrganizationApplicationTCsError(state, action) {
      state.isFetchingOrganizationApplicationTCs = false;
      state.isFetchingOrganizationApplicationTCsSuccess = false;
      state.isFetchingOrganizationApplicationTCsFailed = true;
      state.fetchOrganizationApplicationTCsError = action.payload;
    },
    getDocuments(state, action) {
      state.isFetchingDocuments = true;
      state.isFetchingDocumentsFailed = false;
      state.isFetchingDocumentsSuccess = false;
      state.fetchDocumentResponse = [];
      state.fetchDocumentError = {} as ErrorType;
    },
    setDocuments(state, action) {
      state.isFetchingDocuments = false;
      state.isFetchingDocumentsFailed = false;
      state.isFetchingDocumentsSuccess = true;
      state.fetchDocumentResponse = action.payload;
      state.fetchDocumentError = {} as ErrorType;
    },
    getDocumentsError(state, action) {
      state.isFetchingDocuments = false;
      state.isFetchingDocumentsFailed = true;
      state.isFetchingDocumentsSuccess = false;
      state.fetchDocumentResponse = [];
      state.fetchDocumentError = action.payload;
    },
    getEmploymentIncomeRecords(state, action) {
      state.isFetchingEmploymentIncomeRecords = true;
      state.isFetchingEmploymentIncomeRecordsFailed = false;
      state.isFetchingEmploymentIncomeRecordsSuccess = false;
      state.EmploymentIncomeRecords = [];
      state.fetchEmploymentIncomeRecordsError = {} as ErrorType;
    },
    setEmploymentIncomeRecords(state, action) {
      state.isFetchingEmploymentIncomeRecords = false;
      state.isFetchingEmploymentIncomeRecordsFailed = false;
      state.isFetchingEmploymentIncomeRecordsSuccess = true;
      state.EmploymentIncomeRecords = action.payload;
      state.fetchEmploymentIncomeRecordsError = {} as ErrorType;
    },
    getEmploymentIncomeRecordsError(state, action) {
      state.isFetchingEmploymentIncomeRecords = false;
      state.isFetchingEmploymentIncomeRecordsFailed = true;
      state.isFetchingEmploymentIncomeRecordsSuccess = false;
      state.EmploymentIncomeRecords = [];
      state.fetchEmploymentIncomeRecordsError = action.payload;
    },
    getDeleteEmploymentIncomeRecord(state, action) {
      state.isDeletingEmploymentIncomeRecord = true;
      state.isDeletingEmploymentIncomeRecordFailed = false;
      state.isDeletingEmploymentIncomeRecordSuccess = false;
      state.isDeletingEmploymentIncomeRecordError = {} as ErrorType;
    },
    setDeleteEmploymentIncomeRecord(state, action) {
      state.isDeletingEmploymentIncomeRecord = false;
      state.isDeletingEmploymentIncomeRecordFailed = false;
      state.isDeletingEmploymentIncomeRecordSuccess = true;
      state.isDeletingEmploymentIncomeRecordError = {} as ErrorType;
    },
    getDeleteEmploymentIncomeRecordError(state, action) {
      state.isDeletingEmploymentIncomeRecord = false;
      state.isDeletingEmploymentIncomeRecordFailed = true;
      state.isDeletingEmploymentIncomeRecordSuccess = false;
      state.isDeletingEmploymentIncomeRecordError = action.payload;
    },
    createEmploymentIncomeRecord(state, action) {
      state.isCreatingEmploymentIncomeRecord = true;
      state.isCreatingEmploymentIncomeRecordFailed = false;
      state.isCreatingEmploymentIncomeRecordSuccess = false;
      state.isCreatingEmploymentIncomeRecordError = {} as ErrorType;
    },
    setCreateEmploymentIncomeRecord(state, action) {
      state.isCreatingEmploymentIncomeRecord = false;
      state.isCreatingEmploymentIncomeRecordFailed = false;
      state.isCreatingEmploymentIncomeRecordSuccess = true;
      state.createdEmploymentIncomeRecord = action.payload;
      state.isCreatingEmploymentIncomeRecordError = {} as ErrorType;
    },
    createEmploymentIncomeRecordError(state, action) {
      state.isCreatingEmploymentIncomeRecord = false;
      state.isCreatingEmploymentIncomeRecordFailed = true;
      state.isCreatingEmploymentIncomeRecordSuccess = false;
      state.isCreatingEmploymentIncomeRecordError = action.payload;
    },
    clearCreateEmploymentIncomeRecord(state) {
      //TODO: forgot when this is u
      state.createdEmploymentIncomeRecord = null;
      state.isCreatingEmploymentIncomeRecord = false;
      state.isCreatingEmploymentIncomeRecordFailed = false;
      state.isCreatingEmploymentIncomeRecordSuccess = false;
      state.isCreatingEmploymentIncomeRecordError = {} as ErrorType;
    },
    createRentalHistoryRecord(state) {
      state.isCreatingRentalHistoryRecord = true;
      state.isCreatingRentalHistoryRecordFailed = false;
      state.isCreatingRentalHistoryRecordSuccess = false;
      state.createRentalHistoryRecordError = {} as ErrorType;
    },
    setCreateRentalHistoryRecord(state, action) {
      state.isCreatingRentalHistoryRecord = false;
      state.isCreatingRentalHistoryRecordFailed = false;
      state.isCreatingRentalHistoryRecordSuccess = true;
      state.createdRentalHistoryRecord = action.payload;
    },
    setCreateRentalHistoryRecordError(state, action) {
      state.isCreatingRentalHistoryRecord = false;
      state.isCreatingRentalHistoryRecordFailed = true;
      state.isCreatingRentalHistoryRecordSuccess = false;
      state.createRentalHistoryRecordError = action.payload;
    },
    clearCreateRentalHistoryRecord(state) {
      state.createdRentalHistoryRecord = null;
      state.isCreatingRentalHistoryRecord = false;
      state.isCreatingRentalHistoryRecordFailed = false;
      state.isCreatingRentalHistoryRecordSuccess = false;
      state.createRentalHistoryRecordError = {} as ErrorType;
    },
    getAllRentalHistoryRecords(state) {
      state.isFetchingAllRentalHistoryRecords = true;
      state.isFetchingAllRentalHistoryRecordsFailed = false;
      state.isFetchingAllRentalHistoryRecordsSuccess = false;
      state.fetchAllRentalHistoryRecordsError = {} as ErrorType;
    },
    setAllRentalHistoryRecords(state, action) {
      state.isFetchingAllRentalHistoryRecords = false;
      state.isFetchingAllRentalHistoryRecordsFailed = false;
      state.isFetchingAllRentalHistoryRecordsSuccess = true;
      state.allRentalHistoryRecords = action.payload;
    },
    setFetchAllRentalHistoryRecordsError(state, action) {
      state.isFetchingAllRentalHistoryRecords = false;
      state.isFetchingAllRentalHistoryRecordsFailed = true;
      state.isFetchingAllRentalHistoryRecordsSuccess = false;
      state.fetchAllRentalHistoryRecordsError = action.payload;
    },
    updateEmploymentIncomeRecord(state, action) {
      state.isUpdatingEmploymentIncomeRecord = true;
      state.isUpdatingEmploymentIncomeRecordFailed = false;
      state.isUpdatingEmploymentIncomeRecordSuccess = false;
      state.isUpdatingEmploymentIncomeRecordError = {} as ErrorType;
    },
    setUpdateEmploymentIncomeRecord(state, action) {
      state.isUpdatingEmploymentIncomeRecord = false;
      state.isUpdatingEmploymentIncomeRecordFailed = false;
      state.isUpdatingEmploymentIncomeRecordSuccess = true;
      state.createdEmploymentIncomeRecord = action.payload;
      state.isUpdatingEmploymentIncomeRecordError = {} as ErrorType;
    },
    updateEmploymentIncomeRecordError(state, action) {
      state.isUpdatingEmploymentIncomeRecord = false;
      state.isUpdatingEmploymentIncomeRecordFailed = true;
      state.isUpdatingEmploymentIncomeRecordSuccess = false;
      state.isUpdatingEmploymentIncomeRecordError = action.payload;
    },
    getRentalHistoryRecord(state) {
      state.isFetchingRentalHistoryRecord = true;
      state.isFetchingRentalHistoryRecordFailed = false;
      state.isFetchingRentalHistoryRecordSuccess = false;
      state.fetchRentalHistoryRecordError = {} as ErrorType;
    },
    setRentalHistoryRecord(state, action) {
      state.isFetchingRentalHistoryRecord = false;
      state.isFetchingRentalHistoryRecordFailed = false;
      state.isFetchingRentalHistoryRecordSuccess = true;
      state.fetchedRentalHistoryRecord = action.payload;
    },
    setFetchRentalHistoryRecordError(state, action) {
      state.isFetchingRentalHistoryRecord = true;
      state.isFetchingRentalHistoryRecordFailed = false;
      state.isFetchingRentalHistoryRecordSuccess = false;
      state.fetchRentalHistoryRecordError = action.payload;
    },
    deleteRentalHistoryRecord(state) {
      state.isDeletingRentalHistoryRecord = true;
      state.isDeletingRentalHistoryRecordFailed = false;
      state.isDeletingRentalHistoryRecordSuccess = false;
      state.deleteRentalHistoryRecordError = {} as ErrorType;
    },
    setDeleteRentalHistoryRecord(state) {
      state.isDeletingRentalHistoryRecord = false;
      state.isDeletingRentalHistoryRecordFailed = false;
      state.isDeletingRentalHistoryRecordSuccess = true;
    },
    setDeleteRentalHistoryRecordError(state, action) {
      state.isDeletingRentalHistoryRecord = false;
      state.isDeletingRentalHistoryRecordFailed = true;
      state.isDeletingRentalHistoryRecordSuccess = false;
      state.deleteRentalHistoryRecordError = action.payload;
    },
    updateRentalHistoryRecord(state) {
      state.isUpdatingRentalHistoryRecord = true;
      state.isUpdatingRentalHistoryRecordFailed = false;
      state.isUpdatingRentalHistoryRecordSuccess = false;
      state.updateRentalHistoryRecordError = {} as ErrorType;
    },
    setUpdateRentalHistoryRecord(state, action) {
      state.isUpdatingRentalHistoryRecord = false;
      state.isUpdatingRentalHistoryRecordFailed = false;
      state.isUpdatingRentalHistoryRecordSuccess = true;
    },
    setUpdateRentalHistoryRecordError(state, action) {
      state.isUpdatingRentalHistoryRecord = false;
      state.isUpdatingRentalHistoryRecordFailed = true;
      state.isUpdatingRentalHistoryRecordSuccess = false;
      state.updateRentalHistoryRecordError = action.payload;
    },
    getMinorRecords(state, action) {
      state.isFetchingMinorRecords = true;
      state.isFetchingMinorRecordsFailed = false;
      state.isFetchingMinorRecordsSuccess = false;
      state.MinorRecords = [];
      state.fetchMinorRecordsError = {} as ErrorType;
    },
    setMinorRecords(state, action) {
      state.isFetchingMinorRecords = false;
      state.isFetchingMinorRecordsFailed = false;
      state.isFetchingMinorRecordsSuccess = true;
      state.MinorRecords = action.payload;
      state.fetchMinorRecordsError = {} as ErrorType;
    },
    getMinorRecordsError(state, action) {
      state.isFetchingMinorRecords = false;
      state.isFetchingMinorRecordsFailed = true;
      state.isFetchingMinorRecordsSuccess = false;
      state.MinorRecords = [];
      state.fetchMinorRecordsError = action.payload;
    },
    deleteMinorRecord(state, action) {
      state.isDeletingMinorRecord = true;
      state.isDeletingMinorRecordFailed = false;
      state.isDeletingMinorRecordSuccess = false;
      state.isDeletingMinorRecordError = {} as ErrorType;
    },
    setDeleteMinorRecord(state, action) {
      state.isDeletingMinorRecord = false;
      state.isDeletingMinorRecordFailed = false;
      state.isDeletingMinorRecordSuccess = true;
      state.isDeletingMinorRecordError = {} as ErrorType;
    },
    deleteMinorRecordError(state, action) {
      state.isDeletingMinorRecord = false;
      state.isDeletingMinorRecordFailed = true;
      state.isDeletingMinorRecordSuccess = false;
      state.isDeletingMinorRecordError = action.payload;
    },
    createMinorRecord(state, action) {
      state.isCreatingMinorRecord = true;
      state.isCreatingMinorRecordFailed = false;
      state.isCreatingMinorRecordSuccess = false;
      state.isCreatingMinorRecordError = {} as ErrorType;
    },
    setCreateMinorRecord(state, action) {
      state.isCreatingMinorRecord = false;
      state.isCreatingMinorRecordFailed = false;
      state.isCreatingMinorRecordSuccess = true;
      state.createdMinorRecord = action.payload;
      state.isCreatingMinorRecordError = {} as ErrorType;
    },
    createMinorRecordError(state, action) {
      state.isCreatingMinorRecord = false;
      state.isCreatingMinorRecordFailed = true;
      state.isCreatingMinorRecordSuccess = false;
      state.isCreatingMinorRecordError = action.payload;
    },
    clearCreateMinorRecord(state) {
      //TODO: forgot when this is u
      state.createdMinorRecord = null;
      state.isCreatingMinorRecord = false;
      state.isCreatingMinorRecordFailed = false;
      state.isCreatingMinorRecordSuccess = false;
      state.isCreatingMinorRecordError = {} as ErrorType;
    },
    updateMinorRecord(state, action) {
      state.isUpdatingMinorRecord = true;
      state.isUpdatingMinorRecordFailed = false;
      state.isUpdatingMinorRecordSuccess = false;
      state.isUpdatingMinorRecordError = {} as ErrorType;
    },
    setUpdateMinorRecord(state, action) {
      state.isUpdatingMinorRecord = false;
      state.isUpdatingMinorRecordFailed = false;
      state.isUpdatingMinorRecordSuccess = true;
      state.createdMinorRecord = action.payload;
      state.isUpdatingMinorRecordError = {} as ErrorType;
    },
    updateMinorRecordError(state, action) {
      state.isUpdatingMinorRecord = false;
      state.isUpdatingMinorRecordFailed = true;
      state.isUpdatingMinorRecordSuccess = false;
      state.isUpdatingMinorRecordError = action.payload;
    },
    createApplicantPetRecord(state) {
      state.isCreatingApplicantPetRecord = true;
      state.isCreatingApplicantPetRecordFailed = false;
      state.isCreatingApplicantPetRecordSuccess = false;
      state.createApplicantPetRecordError = {} as ErrorType;
    },
    setCreateApplicantPetRecord(state, action) {
      state.isCreatingApplicantPetRecord = false;
      state.isCreatingApplicantPetRecordFailed = false;
      state.isCreatingApplicantPetRecordSuccess = true;
      state.createdApplicantPetRecord = action.payload;
    },
    setCreateApplicantPetRecordError(state, action) {
      state.isCreatingApplicantPetRecord = false;
      state.isCreatingApplicantPetRecordFailed = true;
      state.isCreatingApplicantPetRecordSuccess = false;
      state.createApplicantPetRecordError = action.payload;
    },
    clearCreateApplicantPetRecord(state) {
      state.createdApplicantPetRecord = null;
      state.isCreatingApplicantPetRecord = false;
      state.isCreatingApplicantPetRecordFailed = false;
      state.isCreatingApplicantPetRecordSuccess = false;
      state.createApplicantPetRecordError = {} as ErrorType;
    },
    getApplicantAllPetRecords(state) {
      state.isFetchingApplicantAllPetRecords = true;
      state.isFetchingApplicantAllPetRecordsFailed = false;
      state.isFetchingApplicantAllPetRecordsSuccess = false;
      state.fetchApplicantAllPetRecordsError = {} as ErrorType;
    },
    setApplicantAllPetRecords(state, action) {
      state.isFetchingApplicantAllPetRecords = false;
      state.isFetchingApplicantAllPetRecordsFailed = false;
      state.isFetchingApplicantAllPetRecordsSuccess = true;
      state.applicantAllPetRecords = action.payload;
    },
    setFetchApplicantAllPetRecordsError(state, action) {
      state.isFetchingApplicantAllPetRecords = false;
      state.isFetchingApplicantAllPetRecordsFailed = true;
      state.isFetchingApplicantAllPetRecordsSuccess = false;
      state.fetchApplicantAllPetRecordsError = action.payload;
    },
    getApplicantPetRecord(state) {
      state.isFetchingApplicantPetRecord = true;
      state.isFetchingApplicantPetRecordFailed = false;
      state.isFetchingApplicantPetRecordSuccess = false;
      state.fetchApplicantPetRecordError = {} as ErrorType;
    },
    setApplicantPetRecord(state, action) {
      state.isFetchingApplicantPetRecord = false;
      state.isFetchingApplicantPetRecordFailed = false;
      state.isFetchingApplicantPetRecordSuccess = true;
      state.applicantPetRecord = action.payload;
    },
    setFetchApplicantlPetRecordError(state, action) {
      state.isFetchingApplicantPetRecord = false;
      state.isFetchingApplicantPetRecordFailed = true;
      state.isFetchingApplicantPetRecordSuccess = false;
      state.fetchApplicantPetRecordError = action.payload;
    },
    deleteApplicantPetRecord(state) {
      state.isDeletingApplicantPetRecord = true;
      state.isDeletingApplicantPetRecordFailed = false;
      state.isDeletingApplicantPetRecordSuccess = false;
      state.deleteApplicantPetRecordError = {} as ErrorType;
    },
    setDeleteApplicantPetRecord(state) {
      state.isDeletingApplicantPetRecord = false;
      state.isDeletingApplicantPetRecordFailed = false;
      state.isDeletingApplicantPetRecordSuccess = true;
    },
    setDeleteApplicantPetRecordError(state, action) {
      state.isDeletingApplicantPetRecord = false;
      state.isDeletingApplicantPetRecordFailed = true;
      state.isDeletingApplicantPetRecordSuccess = false;
      state.deleteApplicantPetRecordError = action.payload;
    },
    clearDeletePetRecord(state) {
      state.isDeletingApplicantPetRecord = false;
      state.isDeletingApplicantPetRecordFailed = false;
      state.isDeletingApplicantPetRecordSuccess = false;
    },
    updateApplicantPetRecord(state) {
      state.isUpdatingApplicantPetRecord = true;
      state.isUpdatingApplicantPetRecordFailed = false;
      state.isUpdatingApplicantPetRecordSuccess = false;
      state.updateApplicantPetRecordError = {} as ErrorType;
    },
    setUpdateApplicantPetRecord(state) {
      state.isUpdatingApplicantPetRecord = false;
      state.isUpdatingApplicantPetRecordFailed = false;
      state.isUpdatingApplicantPetRecordSuccess = true;
    },
    setUpdateApplicantPetRecordError(state, action) {
      state.isUpdatingApplicantPetRecord = false;
      state.isUpdatingApplicantPetRecordFailed = true;
      state.isUpdatingApplicantPetRecordSuccess = false;
      state.updateApplicantPetRecordError = action.payload;
    },
    //vehicles
    createApplicantVehicleRecord(state) {
      state.isCreatingApplicantVehicleRecord = true;
      state.isCreatingApplicantVehicleRecordFailed = false;
      state.isCreatingApplicantVehicleRecordSuccess = false;
      state.createApplicantVehicleRecordError = {} as ErrorType;
    },
    setCreateApplicantVehicleRecord(state, action) {
      state.isCreatingApplicantVehicleRecord = false;
      state.isCreatingApplicantVehicleRecordFailed = false;
      state.isCreatingApplicantVehicleRecordSuccess = true;
      state.createdApplicantVehicleRecord = action.payload;
    },
    setCreateApplicantVehicleRecordError(state, action) {
      state.isCreatingApplicantVehicleRecord = false;
      state.isCreatingApplicantVehicleRecordFailed = true;
      state.isCreatingApplicantVehicleRecordSuccess = false;
      state.createApplicantVehicleRecordError = action.payload;
    },
    clearCreateApplicantVehicleRecord(state) {
      state.createdApplicantVehicleRecord = null;
      state.isCreatingApplicantVehicleRecord = false;
      state.isCreatingApplicantVehicleRecordFailed = false;
      state.isCreatingApplicantVehicleRecordSuccess = false;
      state.createApplicantVehicleRecordError = {} as ErrorType;
    },
    getApplicantAllVehicleRecords(state) {
      state.isFetchingApplicantAllVehicleRecords = true;
      state.isFetchingApplicantAllVehicleRecordsFailed = false;
      state.isFetchingApplicantAllVehicleRecordsSuccess = false;
      state.fetchApplicantAllVehicleRecordsError = {} as ErrorType;
    },
    setApplicantAllVehicleRecords(state, action) {
      state.isFetchingApplicantAllVehicleRecords = false;
      state.isFetchingApplicantAllVehicleRecordsFailed = false;
      state.isFetchingApplicantAllVehicleRecordsSuccess = true;
      state.applicantAllVehicleRecords = action.payload;
    },
    setFetchApplicantAllVehicleRecordsError(state, action) {
      state.isFetchingApplicantAllVehicleRecords = false;
      state.isFetchingApplicantAllVehicleRecordsFailed = true;
      state.isFetchingApplicantAllVehicleRecordsSuccess = false;
      state.fetchApplicantAllVehicleRecordsError = action.payload;
    },
    getApplicantVehicleRecord(state) {
      state.isFetchingApplicantVehicleRecord = true;
      state.isFetchingApplicantVehicleRecordFailed = false;
      state.isFetchingApplicantVehicleRecordSuccess = false;
      state.fetchApplicantVehicleRecordError = {} as ErrorType;
    },
    setApplicantVehicleRecord(state, action) {
      state.isFetchingApplicantVehicleRecord = false;
      state.isFetchingApplicantVehicleRecordFailed = false;
      state.isFetchingApplicantVehicleRecordSuccess = true;
      state.applicantVehicleRecord = action.payload;
    },
    setFetchApplicantVehicleRecordError(state, action) {
      state.isFetchingApplicantVehicleRecord = false;
      state.isFetchingApplicantVehicleRecordFailed = true;
      state.isFetchingApplicantVehicleRecordSuccess = false;
      state.fetchApplicantVehicleRecordError = action.payload;
    },
    deleteApplicantVehicleRecord(state) {
      state.isDeletingApplicantVehicleRecord = true;
      state.isDeletingApplicantVehicleRecordFailed = false;
      state.isDeletingApplicantVehicleRecordSuccess = false;
      state.deleteApplicantVehicleRecordError = {} as ErrorType;
    },
    setDeleteApplicantVehicleRecord(state) {
      state.isDeletingApplicantVehicleRecord = false;
      state.isDeletingApplicantVehicleRecordFailed = false;
      state.isDeletingApplicantVehicleRecordSuccess = true;
    },
    setDeleteApplicantVehicleRecordError(state, action) {
      state.isDeletingApplicantVehicleRecord = false;
      state.isDeletingApplicantVehicleRecordFailed = true;
      state.isDeletingApplicantVehicleRecordSuccess = false;
      state.deleteApplicantVehicleRecordError = action.payload;
    },
    clearDeleteVehicleRecord(state) {
      state.isDeletingApplicantVehicleRecord = false;
      state.isDeletingApplicantVehicleRecordFailed = false;
      state.isDeletingApplicantVehicleRecordSuccess = false;
    },
    updateApplicantVehicleRecord(state) {
      state.isUpdatingApplicantVehicleRecord = true;
      state.isUpdatingApplicantVehicleRecordFailed = false;
      state.isUpdatingApplicantVehicleRecordSuccess = false;
      state.updateApplicantVehicleRecordError = {} as ErrorType;
    },
    setUpdateApplicantVehicleRecord(state) {
      state.isUpdatingApplicantVehicleRecord = false;
      state.isUpdatingApplicantVehicleRecordFailed = false;
      state.isUpdatingApplicantVehicleRecordSuccess = true;
    },
    setUpdateApplicantVehicleRecordError(state, action) {
      state.isUpdatingApplicantVehicleRecord = false;
      state.isUpdatingApplicantVehicleRecordFailed = true;
      state.isUpdatingApplicantVehicleRecordSuccess = false;
      state.updateApplicantVehicleRecordError = action.payload;
    },
    updateApplicantStatus(state, action) {
      state.isUpdatingApplicantStatus = true;
      state.isUpdatingApplicantStatusFailed = false;
      state.isUpdatingApplicantStatusSuccess = false;
      state.isUpdatingApplicantStatusError = {} as ErrorType;
    },
    setUpdateApplicantStatus(state, action) {
      state.isUpdatingApplicantStatus = false;
      state.isUpdatingApplicantStatusFailed = false;
      state.isUpdatingApplicantStatusSuccess = true;
      state.updateApplicantStatusResponse = action.payload;
    },
    setUpdateApplicantStatusError(state, action) {
      state.isUpdatingApplicantStatus = false;
      state.isUpdatingApplicantStatusFailed = true;
      state.isUpdatingApplicantStatusSuccess = false;
      state.isUpdatingApplicantStatusError = action.payload;
    },
    fetchApplications(state, action) {
      state.isFetchingApplications = true;
      state.isFetchingApplicationsSuccess = false;
      state.isFetchingApplicationsFailed = false;
      state.isFetchingApplicationsError = {} as ErrorType;
    },
    setFetchedApplications(state, action) {
      state.isFetchingApplications = false;
      state.isFetchingApplicationsSuccess = true;
      state.isFetchingApplicationsFailed = false;
      state.applicationsFetchResponse = action.payload;
      state.isFetchingApplicationsError = {} as ErrorType;
    },
    setFetchApplicationsError(state, action) {
      state.isFetchingApplications = false;
      state.isFetchingApplicationsSuccess = false;
      state.isFetchingApplicationsFailed = true;
      state.isFetchingApplicationsError = action.payload;
    },
  },
});

export const {
  resetEntireApplicantState,
  getApplicantV2,
  setApplicantV2,
  setApplicantV2Error,
  createApplicantV2,
  setCreateApplicantV2,
  setCreateApplicantV2Error,
  inviteApplicantV2,
  setInviteApplicantV2,
  setInviteApplicantV2Error,
  resetInviteApplicantState,
  getApplicationIntiteeInfoV2,
  setApplicationIntiteeInfoV2,
  setApplicationIntiteeInfoV2Error,
  removeApplicantV2,
  setRemoveApplicantV2,
  setRemoveApplicantV2Error,
  updateApplicantV2,
  setUpdateApplicantV2,
  setUpdateApplicantV2Error,
  getApplicationV2,
  setApplicationV2,
  setApplicationV2Error,
  createApplicationV2,
  setCreateApplicationV2,
  setCreateApplicationV2Error,
  getCustomQuestionsAnswersV2,
  setCustomQuestionsAnswersV2,
  setCustomQuestionsAnswersV2Error,
  updateCustomQuestionsAnswersV2,
  setUpdateCustomQuestionsAnswersV2,
  setUpdateCustomQuestionsAnswersV2Error,
  addAcceptedTCsToJsonV2,
  setAddAcceptedTCsToJsonV2,
  setAddAcceptedTCsToJsonErrorV2,
  getApplicationAcceptedTCsV2,
  setApplicationAcceptedTCsV2,
  setApplicationAcceptedTCsErrorV2,
  updateApplicationV2,
  setUpdateApplicationErrorV2,
  setUpdateApplicationV2,
  getOrganizationApplicationTCs,
  setOrganizationApplicationTCs,
  setOrganizationApplicationTCsError,
  getDocuments,
  setDocuments,
  getDocumentsError,
  getEmploymentIncomeRecords,
  setEmploymentIncomeRecords,
  getEmploymentIncomeRecordsError,
  getDeleteEmploymentIncomeRecord,
  setDeleteEmploymentIncomeRecord,
  getDeleteEmploymentIncomeRecordError,
  createEmploymentIncomeRecord,
  setCreateEmploymentIncomeRecord,
  createEmploymentIncomeRecordError,
  clearCreateEmploymentIncomeRecord,
  createRentalHistoryRecord,
  setCreateRentalHistoryRecord,
  setCreateRentalHistoryRecordError,
  clearCreateRentalHistoryRecord,
  getAllRentalHistoryRecords,
  setAllRentalHistoryRecords,
  setFetchAllRentalHistoryRecordsError,
  updateEmploymentIncomeRecord,
  setUpdateEmploymentIncomeRecord,
  updateEmploymentIncomeRecordError,
  getRentalHistoryRecord,
  setFetchRentalHistoryRecordError,
  setRentalHistoryRecord,
  deleteRentalHistoryRecord,
  setDeleteRentalHistoryRecord,
  setDeleteRentalHistoryRecordError,
  updateRentalHistoryRecord,
  setUpdateRentalHistoryRecord,
  setUpdateRentalHistoryRecordError,
  getMinorRecords,
  setMinorRecords,
  getMinorRecordsError,
  createMinorRecord,
  setCreateMinorRecord,
  createMinorRecordError,
  updateMinorRecord,
  setUpdateMinorRecord,
  updateMinorRecordError,
  deleteMinorRecord,
  setDeleteMinorRecord,
  deleteMinorRecordError,
  clearCreateMinorRecord,
  createApplicantPetRecord,
  setCreateApplicantPetRecord,
  setCreateApplicantPetRecordError,
  clearCreateApplicantPetRecord,
  setApplicantAllPetRecords,
  setFetchApplicantAllPetRecordsError,
  getApplicantAllPetRecords,
  setApplicantPetRecord,
  getApplicantPetRecord,
  setFetchApplicantlPetRecordError,
  deleteApplicantPetRecord,
  setDeleteApplicantPetRecord,
  setDeleteApplicantPetRecordError,
  clearDeletePetRecord,
  clearUpdateApplicantV2,
  updateApplicantPetRecord,
  setUpdateApplicantPetRecord,
  setUpdateApplicantPetRecordError,
  clearCreateApplicantVehicleRecord,
  clearDeleteVehicleRecord,
  createApplicantVehicleRecord,
  getApplicantAllVehicleRecords,
  getApplicantVehicleRecord,
  deleteApplicantVehicleRecord,
  setDeleteApplicantVehicleRecord,
  setDeleteApplicantVehicleRecordError,
  setApplicantVehicleRecord,
  setApplicantAllVehicleRecords,
  setCreateApplicantVehicleRecord,
  setCreateApplicantVehicleRecordError,
  setFetchApplicantAllVehicleRecordsError,
  setFetchApplicantVehicleRecordError,
  setUpdateApplicantVehicleRecord,
  setUpdateApplicantVehicleRecordError,
  updateApplicantVehicleRecord,
  setUpdateApplicantStatus,
  setUpdateApplicantStatusError,
  updateApplicantStatus,
  fetchApplications,
  setFetchedApplications,
  setFetchApplicationsError,
} = applicantsSlice.actions;

export default applicantsSlice.reducer;

export const selectApplicantsState = (state: RootState) => state.applicantsState;

/*
TODO: find a better home for him.
seems like we need to define a type for our UI CustomDropZone widget guy. 
Although it should implement File, all we really need is name and uuid
*/
export type applicantDocumentMetadata = {
  name: string;
  remoteUuid: string;
};
